import { useState } from 'react';
import { Rocket } from 'lucide-react';
import styles from './ReturnCalculator.module.css';

const DEPOSIT = 100_000;
const ACTIVATION_FEE = 7_000;
// Selectable monthly rates, as percentages. The slider hands over 1, 2 or 3.
const STOPS = [1, 2, 3];
const MIN_RATE = STOPS[0];
const MAX_RATE = STOPS[STOPS.length - 1];

/**
 * Return on the client's capital actually at risk — the activation fee — rather
 * than on the funded deposit.
 */
export function returnOnCollateralPct(monthlyRatePercent) {
  const monthlyRate = monthlyRatePercent / 100; // 0.01, 0.02, 0.03

  // Trading profit generated on the funded account
  const annualProfit = DEPOSIT * monthlyRate * 12;

  // Return on the client's actual capital at risk (the $7K)
  const returnOnCollateral = annualProfit / ACTIVATION_FEE;
  return (returnOnCollateral - 1) * 100;
}

function ReturnCalculator() {
  const [rate, setRate] = useState(MIN_RATE);

  // The track runs from 0 so the first stop sits a third of the way along, as
  // designed; the value itself is clamped to the 1-3% range.
  const fillPercent = (rate / MAX_RATE) * 100;

  return (
    <section className={`${styles.section} row align-items-center justify-content-center g-4 g-lg-5`}>
      <div className="col-12 col-lg-auto d-flex justify-content-center">
        <span className={`${styles.iconCircle} d-flex align-items-center justify-content-center`}>
          <Rocket size={120} strokeWidth={1.5} aria-hidden="true" />
        </span>
      </div>

      <div className={`col-12 col-lg-7 text-center ${styles.textCol}`}>
        <h2 className={styles.headline}>
          Your account
          <br />
          could generate an estimated{' '}
          <span className={styles.figure}>{returnOnCollateralPct(rate).toFixed(2)}%</span>{' '}
          annual gross return
        </h2>

        <p className={`${styles.caption} mt-3 mb-4`}>
          <strong className={styles.captionStrong}>{rate}% a month</strong>, net of activation fees
        </p>

        <div className={`${styles.sliderWrap} mx-auto`}>
          <input
            type="range"
            min={0}
            max={MAX_RATE}
            step={1}
            value={rate}
            onChange={(event) => setRate(Math.max(MIN_RATE, Number(event.target.value)))}
            className={styles.slider}
            style={{ '--fill': `${fillPercent}%` }}
            aria-label="Assumed monthly return"
            aria-valuetext={`${rate}% a month`}
          />
          <div className={styles.ticks} aria-hidden="true">
            {STOPS.map((stop) => (
              <span
                key={stop}
                className={`${styles.tick} ${stop === rate ? styles.tickActive : ''}`}
                style={{ left: `${(stop / MAX_RATE) * 100}%` }}
              >
                {stop}%
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ReturnCalculator;
